import { motion } from "framer-motion";
import { HandHeart, Gift, Handshake, ArrowUpRight } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { ORG } from "../data/content";
import { openWhatsApp } from "../utils/scroll";

const ease = [0.22, 1, 0.36, 1];

const OPTIONS = [
  {
    icon: HandHeart,
    title: "Volunteer",
    desc: "Give your time and skills to make a difference.",
    cta: "Join as Volunteer",
    message: "Namaste Hope Giver team! I would like to volunteer with you. Please share the next steps.",
    testid: "involved-volunteer-btn",
    highlight: false,
  },
  {
    icon: Gift,
    title: "Donate / Support",
    desc: "Help us provide essential support to people who need it.",
    cta: "Donate Now",
    message: "Namaste Hope Giver team! I would like to support Hope Giver's mission. Please guide me on how I can contribute.",
    testid: "involved-donate-btn",
    highlight: true,
  },
  {
    icon: Handshake,
    title: "Partner With Us",
    desc: "Work with Hope Giver to create larger community impact.",
    cta: "Become a Partner",
    message: "Namaste Hope Giver team! We would love to partner with Hope Giver to create greater community impact.",
    testid: "involved-partner-btn",
    highlight: false,
  },
];

export default function GetInvolved() {
  return (
    <section id="get-involved" className="bg-sand py-16 sm:py-24 lg:py-32" data-testid="get-involved-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <SectionHeading
          align="center"
          eyebrow="Get Involved"
          title="Three ways to"
          accent="give hope."
          sub="However you choose to help — time, support or partnership — it reaches the people who need it most. Tap a card to start on WhatsApp."
        />

        <div className="mt-14 grid gap-6 md:grid-cols-3 lg:gap-8">
          {OPTIONS.map((o, i) => (
            <motion.div
              key={o.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: 0.12 * i, ease }}
              whileHover={{ y: -10 }}
              className={`group relative flex h-full flex-col overflow-hidden rounded-[2rem] p-8 transition-shadow duration-300 ${
                o.highlight
                  ? "bg-[#0D4B56] text-white shadow-[0_24px_60px_rgba(13,75,86,0.35)]"
                  : "bg-white text-[#0F172A] shadow-[0_10px_30px_rgba(13,75,86,0.08)] hover:shadow-[0_24px_50px_rgba(13,75,86,0.15)]"
              }`}
            >
              {o.highlight && (
                <span className="absolute right-6 top-6 rounded-full gold-shimmer px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em] text-[#08353D]">
                  Most needed
                </span>
              )}
              <span
                className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl transition-colors duration-300 ${
                  o.highlight
                    ? "bg-white/10 text-[#F3E096]"
                    : "bg-[#E6F0F2] text-[#0D4B56] group-hover:bg-[#D4AF37] group-hover:text-[#08353D]"
                }`}
              >
                <o.icon size={28} />
              </span>
              <h3 className="mt-6 font-display text-2xl font-bold">{o.title}</h3>
              <p className={`mt-3 flex-1 leading-relaxed ${o.highlight ? "text-white/70" : "text-[#475569]"}`}>
                {o.desc}
              </p>
              <a
                href={`https://wa.me/${ORG.whatsappNumber}`}
                onClick={(e) => {
                  e.preventDefault();
                  openWhatsApp(ORG.whatsappNumber, o.message);
                }}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={o.testid}
                className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-all duration-300 hover:gap-3 ${
                  o.highlight
                    ? "gold-shimmer text-[#08353D]"
                    : "bg-[#0D4B56] text-[#F3E096] hover:bg-[#08353D]"
                }`}
              >
                {o.cta} <ArrowUpRight size={16} />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
